import { ErrorBoundary } from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, App } from "antd";
import { LucideProvider } from "lucide-react";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { Controller } from "stimulus";

import ErrorFallback from "components/core/ErrorFallback";
import { Toaster } from "components/core/Toaster";
import { queryClient } from "helpers/queryClient";
import { CompletionModalProvider } from "providers/CompletionModalProvider";
import { HarvestSheetProvider } from "providers/HarvestSheetProvider";
import { useAntdTheme } from "theme/antd";

const exposedComponents = {
  DesktopSidebar: () => import("components/DesktopSidebar"),
  GardenSettings: () => import("components/GardenSettings"),
  GardenSelector: () => import("components/GardenSelector"),
  CalendarSidebar: () => import("components/CalendarSidebar"),
  CalendarNavbar: () => import("components/CalendarNavbar"),
  MonthCalendar: () => import("components/MonthCalendar"),
  YearCalendarLane: () => import("components/YearCalendarLane"),
  Flash: () => import("components/Flash"),
  CookiesWarning: () => import("components/CookiesWarning"),
  SneakyBrowsersWarning: () => import("components/SneakyBrowsersWarning"),
  HourlyWeatherToolbar: () => import("components/HourlyWeatherToolbar"),
  TaskFilter: () => import("components/TaskFilter"),
  ViewSelector: () => import("components/ViewSelector"),
  Profile: () => import("components/Profile"),
  Subscriptions: () => import("components/SubscriptionsPage"),
  BillingPortal: () => import("components/BillingPortal"),
  YearTimelineNavigation: () => import("components/CalendarMenu/YearTimelineNavigation"),
  MonthNavigation: () => import("components/CalendarMenu/MonthNavigation"),
  Journal: () => import("components/Journal"),
  Tasks: () => import("components/Tasks"),
  CalendarTaskModal: () => import("components/CalendarTaskModal"),
  Customize: () => import("components/Customize"),
  Community: () => import("components/Community"),
  GardenPlan: () => import("components/GardenPlan"),
  FPSStats: () => import("react-fps-stats"),
  SignUpLocation: () => import("components/SignUpLocation"),
  UpgradeToLayersModal: () => import("components/UpgradeToLayersModal"),
  UpgradeToManageInventoryModal: () => import("components/UpgradeToManageInventoryModal"),
  UpgradeToAccessRecordsModal: () => import("components/UpgradeToAccessRecordsModal"),
  ManualCancellationRequiredModal: () =>
    import("components/ManualCancellationRequiredModal"),
  InviteFriends: () => import("components/InviteFriends"),
  Timeline: () => import("components/Timeline"),
  MobileHeader: () => import("components/MobileHeader"),
  Inventory: () => import("components/Inventory"),
  Records: () => import("components/Records"),
  Dashboard: () => import("components/Dashboard"),
  SequenceTimelineEditor: () => import("components/admin/SequenceTimelineEditor")
};

const TaskCompletionProviders = ({ children }) => (
  <CompletionModalProvider>
    <HarvestSheetProvider>{children}</HarvestSheetProvider>
  </CompletionModalProvider>
);

const EntryPoint = ({ children }) => {
  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider prefixCls="ant6" theme={useAntdTheme()}>
          <LucideProvider size={20}>
            <App>
              <TaskCompletionProviders>{children}</TaskCompletionProviders>
            </App>
          </LucideProvider>
        </ConfigProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

let toasterElement;
let toasterRoot;

const mountToaster = () => {
  if (toasterElement?.isConnected) return;

  toasterRoot?.unmount();
  toasterElement = document.createElement("div");
  document.body.appendChild(toasterElement);
  toasterRoot = createRoot(toasterElement);
  toasterRoot.render(createElement(Toaster));
};

export default class extends Controller {
  initialize() {
    this.elementData = this.element.dataset;
    this.root = createRoot(this.element);
  }

  connect() {
    mountToaster();
    this.importComponent().then((component) => {
      if (!this.element.isConnected) return;

      this.root.render(
        createElement(EntryPoint, {}, createElement(component, this.componentProps()))
      );
    });
  }

  disconnect() {
    this.root.unmount();
  }

  importComponent() {
    if (!this.elementData.component) {
      throw new Error(
        `You should define the exposed component name
        via 'data-component' attribute to use this controller`
      );
    }

    if (!exposedComponents[this.elementData.component]) {
      throw new Error(
        `${this.element.getAttribute(
          "data-component"
        )} wasn't registered in exposedComponents`
      );
    }

    return exposedComponents[this.elementData.component]().then(
      (module) => module.default
    );
  }

  componentProps() {
    return JSON.parse(this.elementData.props);
  }
}
